import { AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from "@angular/core";
import { findEmojiShortcodes } from "./emoji-shortcodes";

interface IEmojiCategory {
	/** Font Awesome icon for the category's tab. */
	icon: string;
	name: string;
	emojis: string[];
}

/**
 * A small emoji keyboard: category tabs across the top, a grid of emojis under them, and the picked
 * one emitted back to whoever opened it.  Deliberately a hand-picked list rather than a full emoji
 * library -- these are the ones people reach for in a work chat, and it keeps the bundle untouched.
 *
 * The host owns the showing and hiding of it (a popover on desktop, a sheet above the keyboard on
 * mobile); this component is only the panel itself.
 */
@Component({
	selector: "emoji-picker",
	templateUrl: "emoji-picker.component.html",
	styleUrls: ["emoji-picker.component.css"],
	standalone: false
})
export class EmojiPickerComponent implements AfterViewInit {
	@ViewChild("searchRef") searchRef: ElementRef;

	@Output("emojiSelected") emojiSelectedEmitter: EventEmitter<string> = new EventEmitter<string>();

	/**
	 * Whether opening the panel puts the cursor in the search box, ready to type a name.  On by
	 * default -- the panel is opened deliberately, and searching is the quickest way through it.
	 *
	 * Off is for anywhere an on-screen keyboard would come up with the focus and cover the grid the
	 * user just opened, which is why the mobile page turns it off.
	 */
	@Input() focusSearchOnOpen: boolean = true;

	activeCategoryIndex: number = 0;

	/**
	 * Searching by name, for when you know what you want and don't fancy reading six tabs to find it.
	 * The names are the same ones the composer's ":" autocomplete completes, so "wink" turns up the
	 * same emoji whichever way it's asked for.
	 *
	 * Results are worked out as the text changes rather than by a getter: a getter hands *ngFor a new
	 * array every change detection run, which rebuilds every button in the grid.
	 */
	searchText: string = "";
	searchResults: string[] = [];

	categories: IEmojiCategory[] = [
		{
			name: "Smileys",
			icon: "fa-face-smile",
			emojis: [
				"😀", "😃", "😄", "😁", "😆", "😅", "🤣", "😂", "🙂", "🙃",
				"😉", "😊", "😇", "😍", "😘", "😗", "😋", "😜", "🤪", "🤨",
				"🧐", "🤓", "😎", "🥳", "😏", "😒", "😞", "😔", "😟", "😕",
				"🙁", "😣", "😖", "😫", "😩", "🥺", "😢", "😭", "😤", "😠",
				"😡", "🤬", "🤯", "😳", "🥵", "🥶", "😱", "😨", "😰", "😥",
				"😓", "🤗", "🤔", "🤭", "🤫", "😬", "🙄", "😯", "😴", "🤮",
				"🤧", "😷", "🤒", "🤕", "🥴", "😵", "🤠", "🤡", "👻", "💀"
			]
		},
		{
			name: "Gestures",
			icon: "fa-thumbs-up",
			emojis: [
				"👍", "👎", "👌", "🤌", "✌️", "🤞", "🤟", "🤘", "🤙", "👈",
				"👉", "👆", "👇", "☝️", "✋", "🤚", "🖐️", "🖖", "👋", "🤝",
				"🙏", "✍️", "💪", "🦾", "👏", "🙌", "👐", "🤲", "🫡", "🫰",
				"👊", "✊", "🤛", "🤜", "🖕", "💅", "👀", "🧠", "🗣️", "👤"
			]
		},
		{
			name: "Hearts",
			icon: "fa-heart",
			emojis: [
				"❤️", "🧡", "💛", "💚", "💙", "💜", "🖤", "🤍", "🤎", "💔",
				"❣️", "💕", "💞", "💓", "💗", "💖", "💘", "💝", "💯", "💢",
				"💥", "💫", "💦", "💨", "🔥", "✨", "⭐", "🌟", "⚡", "🎉"
			]
		},
		{
			name: "Work",
			icon: "fa-helmet-safety",
			emojis: [
				"🏠", "🏢", "🏗️", "🚧", "🧰", "🔨", "🪛", "🔧", "🪚", "🪜",
				"🧯", "🧹", "🧽", "🪣", "🚿", "💧", "🌊", "🌧️", "⛈️", "🌪️",
				"❄️", "🥶", "🌡️", "🔌", "💡", "🔋", "📏", "📐", "🗑️", "♻️",
				"🚚", "🚐", "🛻", "🚗", "✈️", "⛽", "🧪", "🦠", "☣️", "⚠️",
				"📋", "📝", "📄", "📇", "📦", "📸", "🔑", "🗝️", "🛠️", "⚙️"
			]
		},
		{
			name: "Time & money",
			icon: "fa-clock",
			emojis: [
				"⏰", "⏱️", "⌚", "📅", "🗓️", "⏳", "⌛", "🔔", "🔕", "📞",
				"📱", "💻", "🖥️", "🖨️", "📧", "📨", "💬", "💭", "📊", "📈",
				"📉", "💰", "💵", "💳", "🧾", "🏦", "✅", "❌", "❗", "❓",
				"🔴", "🟠", "🟡", "🟢", "🔵", "⚪", "⚫", "🚩", "📍", "🎯"
			]
		},
		{
			name: "Food & fun",
			icon: "fa-mug-hot",
			emojis: [
				"☕", "🍵", "🥤", "🍺", "🍻", "🥂", "🍕", "🍔", "🌮", "🌯",
				"🍟", "🥪", "🥗", "🍩", "🍪", "🎂", "🍰", "🍫", "🍎", "🍌",
				"🎁", "🎈", "🏆", "🥇", "🎊", "🎵", "🎧", "🏈", "⚽", "🏀",
				"🐶", "🐱", "🌞", "🌙", "🌈", "🌴", "🏖️", "🚀", "🎣", "🎸"
			]
		}
	];

	/* The panel is created fresh every time it's opened (the host *ngIf's it away when closed), so
	 * this runs on each open rather than only on the first. */
	ngAfterViewInit() {
		if (this.focusSearchOnOpen)
			setTimeout(() => this.searchRef?.nativeElement?.focus());
	}

	/** Whether the grid is showing search results rather than the open category's emojis. */
	get isSearching(): boolean {
		return (this.searchText || "").trim().length > 0;
	}

	onSearchChanged() {
		this.searchResults = findEmojiShortcodes(this.searchText).map(x => x.emoji);
	}

	clearSearch() {
		this.searchText = "";
		this.searchResults = [];
	}

	selectCategory(categoryIndex: number) {
		this.activeCategoryIndex = categoryIndex;
	}

	selectEmoji(emoji: string) {
		this.emojiSelectedEmitter.emit(emoji);
	}
}
